<template>
  <div>
    <b-form-group>
      <label :for="id">{{ label }} </label>
      <validation-provider
        #default="{ errors }"
        :name="errorName"
        :rules="rules"
        :vid="vid"
      >
        <div class="d-flex align-items-center">
          <b-form-input
            :id="id"
            v-model="innerValue"
            :for="id"
            :type="type"
            :placeholder="placeholder"
            :readonly="readonly"
            :value="value"
            :state="errors.length > 0 ? false : null"
            :step="step"
            :onpaste="onpaste"
            :formatter="formatter"
            autocomplete="off"
            :on-mousedown="disableCopyPaste"
            @change="onChange"
            @input="onInput"
            @blur="onBlur"
            @keypress.native="handleKeyPress"
            @keydown.native="disableCopyPaste"
          />
          <p
            v-if="text"
            class="ml-1"
          >
            {{ text }}
          </p>
          <feather-icon
            v-show="showIcon"
            :icon="icon"
            :size="iconSize"
            class="cursor-pointer ml-1"
            @click="onClick"
          />
        </div>

        <small class="text-danger error-text">{{ errors[0] }}</small>
      </validation-provider>
    </b-form-group>
  </div>
</template>

<script>
import { BFormInput, BFormGroup } from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import { sweetAlert } from '@core/mixins/sweetAlert'
import {
  removeSpecialChar,
  removeNegativeNumber,
  removeCharAndSpace,
} from '../../helpers/handleSpecialCharacter'

const inputTypes = [
  'text',
  'email',
  'password',
  'number',
  'tel',
  'search',
  'color',
]
export default {
  name: 'InputField',
  components: {
    BFormInput,
    BFormGroup,
    ValidationProvider,
  },
  mixins: [sweetAlert],
  props: {
    label: {
      type: String,
      required: true,
    },
    errorName: {
      type: String,
      default: 'default',
    },
    onpaste: {
      type: String,
      default: null,
      required: false,
    },
    rules: {
      type: [Object, String],
      default: '',
    },
    id: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: null,
    },
    text: {
      type: String,
      required: false,
      default: null,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    value: {
      type: null,
      default: '',
    },
    step: {
      type: Number,
      required: false,
      default: null,
    },
    onInput: {
      type: Function,
      default: () => {},
      required: false,
    },
    onChange: {
      type: Function,
      default: () => {},
      required: false,
    },
    onBlur: {
      type: [Function, Number, String],
      default: () => {},
      required: false,
    },
    onClick: {
      type: Function,
      default: () => {},
      required: false,
    },
    type: {
      type: String,
      required: true,
      default: 'text',
      validator: value => inputTypes.includes(value),
    },
    icon: {
      type: String,
      default: 'AlertCircleIcon',
      required: false,
    },
    iconSize: {
      type: String,
      default: '16',
      required: false,
    },
    showIcon: {
      type: Boolean,
      default: false,
      required: false,
    },
    vid: {
      type: String,
      default: '',
    },
    formatter: {
      type: Function,
      default: null,
      required: false,
    },
    isAllowSpecialCharacters: {
      type: Boolean,
      default: true,
      required: false,
    },
    isAllowOnlySpecialCharacters: {
      type: Boolean,
      default: true,
      required: false,
    },
    isAllowCopyAndPaste: {
      type: Boolean,
      default: true,
      required: false,
    },
    isAllowNegativeNumbers: {
      type: Boolean,
      default: true,
      required: false,
    },
    maxLength: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      innerValue: '',
    }
  },
  computed: {
    hasValue() {
      return !!this.innerValue
    },
  },
  watch: {
    innerValue(value) {
      this.$emit('input', value)
    },
    value(val) {
      if (val !== this.innerValue) {
        this.innerValue = val
      }
    },
  },
  created() {
    if (this.value) {
      this.innerValue = this.value
    }
  },
  methods: {
    validateInputLength(event, maxLength) {
      if (event.target.value && event.target.value.length > maxLength) {
        // Prevent default behavior
        event.preventDefault()
        return false
      }
      return true
    },
    // eslint-disable-next-line consistent-return
    handleKeyPress(event) {
      if (!this.isAllowSpecialCharacters) {
        removeSpecialChar(event)
      }
      if (!this.isAllowOnlySpecialCharacters) {
        removeCharAndSpace(event)
      }
      if (this.type === 'number' && !this.isAllowNegativeNumbers) {
        removeNegativeNumber(event)
      }
      if (this.maxLength) {
        this.validateInputLength(event, this.maxLength)
      }
    },
    // eslint-disable-next-line consistent-return
    disableCopyPaste(event) {
      if (!this.isAllowSpecialCharacters || !this.isAllowCopyAndPaste) {
        const kCode = event.keyCode || event.charCode
        if ((kCode === 86 && event.ctrlKey) || kCode === 0) {
          this.alertInfo({ text: 'Copy and paste not allowed' })
          return false
        }
      }
    },
  },
}
</script>

<style scoped>
.error-text {
  padding-top: 0px;
}
</style>
